/* Copyright (c) 2020-present CloudIO Inc */

import { emptyFunction } from '@cloudio-saas/statex';
import { Platform } from '../../platform/Platform';
import {
  ActionManager,
  ActionManagerFn,
  AliasType,
  RequiredItem,
} from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { returnType } from '../ActionSet/ActionSetManager';
import { defaultValue } from './ActionUpdateRecord';

const ActionUpdateRecordManager: ActionManagerFn<'ActionUpdateRecord'> = (
  platform: Platform,
): ActionManager<'ActionUpdateRecord'> => {
  let callback = emptyFunction;
  let expressionCallback = emptyFunction;
  return {
    ...baseActionManager(platform, 'ActionUpdateRecord'),
    defaultValue,
    props: {
      ...baseActionProps,
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'TransientDataStore',
        ],
      },
      targetAttribute: {
        label: 'Target Attribute',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        required: true,
        getAttributeDataTypes: () => 'ALL',
        preventMultiEdit: true,
        attributeRefreshCallback: (cb) => {
          callback = cb;
        },
      },
      valueExpression: {
        label: 'Value Expression',
        type: 'expression',
        required: true,
        refreshCallback: (cb) => {
          expressionCallback = cb;
        },
        returnType: async (
          item: RequiredItem<'ActionUpdateRecord'>,
          { appUid, pageId, itemId },
        ) => {
          return returnType({
            appUid,
            pageId,
            itemId,
            attribute: item.props.targetAttribute,
            itemType: item.type,
            platform,
            storeType: 'STORE',
          });
        },
      },
    },
    onPropertyChange: (property, value) => {
      if (property === 'alias') {
        callback();
        return {
          [property]: value,
          targetAttribute: defaultValue.targetAttribute,
          valueExpression: defaultValue.valueExpression,
        };
      }
      if (property === 'targetAttribute') {
        expressionCallback();
        return {
          [property]: value,
          valueExpression: defaultValue.valueExpression,
        };
      }
      return {
        [property]: value,
      };
    },
    isPropertyVisible(prop, props): boolean {
      switch (prop) {
        case 'targetAttribute':
          return !!props.alias;
        case 'valueExpression':
          return !!props.alias && !!props.targetAttribute;
        default:
          return true;
      }
    },
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'STORE'
        ? { aliasProp: 'alias', embeddedPageIdProp: 'embeddedPageId' } // TODO: Add embeddedPageId prop
        : undefined,
    isDataSourceAliasUsed: (item, alias) => item.props.alias === alias,
    name: 'Update Record',
    getDisplayText: (item) =>
      item.props.alias
        ? `${item.props.targetAttribute ?? ''} (${item.props.alias})`
        : '',
    icon: { iconName: 'edit' },
    description: `**Overview**<br/>
The Update Record action is a data manipulation component designed to programmatically modify a specific field within the current record of a Data Store. Unlike ActionSet, which is restricted to specific parent contexts (like loop or create), this action can be used universally anywhere in the application logic to alter data.<br/>
This component modifies the local state of the Data Store immediately. This "dirty" state (the change) is then held in memory until an ActionSave is executed to persist it to the database, or an ActionReset is called to discard it.

**Key Features**<br/>
* **Targeted Updates:** Specifically modifies a single attribute (column) of the record currently active/selected in the specified Data Store.
* **Broad Store Support:** Compatible with CloudDataStore, DataStore, LocalDataStore, and TransientDataStore, making it useful for updating both persistent database records and temporary form data.
* **Type Safety:** Implements dynamic validation logic (returnType) to ensure that the data type of the Value Expression matches the schema of the Target Attribute (e.g., preventing a string from being written to a Date field).
* **Smart Visibility:** The property panel is context-aware, hiding the Attribute selector until an Alias is chosen, and hiding the Value input until an Attribute is chosen, ensuring a valid configuration flow.

**Properties**<br/>
* **Alias (alias):** DataSourceAliasChooser
  * *Description:* Selects the Data Store containing the record to be updated.
  * *Constraint:* Must be a valid store type (Cloud, Local, Transient).
* **Target Attribute (targetAttribute):** ViewAttributeChooser
  * *Description:* The specific field/column in the current record that will receive the new value.
* **Value Expression (valueExpression):** Expression
  * *Description:* The logic defining the new data value.
  * *Validation:* The expected return type (String, Number, Boolean, Date) is enforced based on the selected Target Attribute.

**Use Cases**<br/>
* **Calculated Fields:** Automatically updating a "Total Price" field in an order record whenever the "Quantity" or "Unit Price" fields are changed by the user.
* **Status Toggles:** A custom "Approve" button that uses this action to set the Status field of the current record to 'Approved' and the ApprovedDate to Now().
* **Data Cleanup:** Trimming whitespace or formatting a phone number field automatically when a user tabs out of an input.
* **Conditional Logic:** Setting a "HasDependents" flag to false if the user enters "0" in the "Number of Children" field.`,
  };
};

export default ActionUpdateRecordManager;
